import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material'
import axios from 'axios'
import { v4 as uuidv4 } from 'uuid'

type Company = {
  id: string
  name: string
}

export const AddReview = ({ open, onClose }) => {
  const [formData, setFormData] = useState({
    company_id: '',
    user: '',
    rating: '',
    comment: '',
    camp_name: ''
  })

  const [companies, setCompanies] = useState<Company[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (open) {
      const fetchCompanies = async () => {
        setIsLoading(true)
        try {
          const response = await axios.get(
              `${process.env.REACT_APP_WAKACJE_API_URL}/v1/companies?limit=1000`
          )
          setCompanies(response.data.data || [])
        } catch (error) {
          console.error('Błąd podczas pobierania firm:', error)
        } finally {
          setIsLoading(false)
        }
      }
      fetchCompanies()
    }
  }, [open])

  const handleSubmit = async e => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      await axios.post(
          `${process.env.REACT_APP_WAKACJE_API_URL}/v1.1/admin/companies/${formData.company_id}/reviews`,
          {
            user: formData.user,
            rating: parseInt(formData.rating),
            comment: formData.comment,
            camp_name: formData.camp_name,
            company_id: formData.company_id,
            id: uuidv4(),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }
      )
      onClose()
    } catch (error) {
      console.error('Błąd podczas dodawania recenzji:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  return (
      <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
        <DialogTitle>Dodaj nową recenzję</DialogTitle>
        <DialogContent>
          <Box component='form' onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <FormControl fullWidth margin='normal' required disabled={isLoading}>
              <InputLabel id='company-select-label'>Firma</InputLabel>
              <Select
                  labelId='company-select-label'
                  name='company_id'
                  value={formData.company_id}
                  label='Firma'
                  onChange={handleChange}
              >
                {isLoading && (
                    <MenuItem value='' disabled>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={20} />
                        <span>Ładowanie firm...</span>
                      </Box>
                    </MenuItem>
                )}
                {companies.map(company => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
                fullWidth
                label='Użytkownik'
                name='user'
                value={formData.user}
                onChange={handleChange}
                required
                margin='normal'
            />
            <TextField
                fullWidth
                label='Ocena (1-5)'
                name='rating'
                type='number'
                inputProps={{ min: 1, max: 5 }}
                value={formData.rating}
                onChange={handleChange}
                required
                margin='normal'
            />
            <TextField
                fullWidth
                label='Komentarz'
                name='comment'
                value={formData.comment}
                onChange={handleChange}
                multiline
                rows={4}
                margin='normal'
            />
            <TextField
                fullWidth
                label='Nazwa obozu'
                name='camp_name'
                value={formData.camp_name}
                onChange={handleChange}
                margin='normal'
            />
            <Box
                sx={{
                  mt: 3,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 2
                }}
            >
              <Button onClick={onClose} disabled={isSubmitting}>
                Anuluj
              </Button>
              <Button
                  type='submit'
                  variant='contained'
                  disabled={isLoading || isSubmitting}
              >
                {isSubmitting ? <CircularProgress size={24} /> : 'Dodaj'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
  )
}
